export * from './redux';
import { SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { ManipulateType } from 'dayjs';
import { FilterProp, FiltersProp, FilterType, SelectedFilters } from '@constants/data-table';
import { UserRoles, UserRolesMap } from '@constants/enum/common';

export const CREATE_API_KEY_MODAL = {
  TITLE: 'Create API Key',
  FORM_ITEMS: {
    NAME: {
      LABEL: 'Name',
      PLACEHOLDER: 'Type key name',
      HELP_TEXT: 'The key ID is used if no name is provided.',
    },
    EXPIRES_AT: {
      LABEL: 'API key expiration date',
      PLACEHOLDER: 'Select date',
    },
    EXPIRES: {
      LABEL: 'Never expires',
    },
    ROLE: {
      LABEL: 'Role',
    },
    WORKSPACES: {
      LABEL: 'Workspaces',
    },
  },
  BUTTON_TEXT: 'Create API Key',
};

export const EDIT_API_KEY_MODAL = {
  TITLE: 'Edit API Key',
  INFO_TEXT: 'Role changes may take a few minutes to take effect.',
  DESCRIPTION: 'Use this key to interact with deepset AI platform.',
  FORM_ITEMS: {
    NAME: {
      LABEL: 'Name',
      PLACEHOLDER: 'Enter name',
    },
    ROLE: {
      LABEL: 'Role',
    },
  },
  BUTTON_TEXT: 'Save',
};

export const API_KEY_PREVIEW = {
  MODAL_TITLE: 'API key created',
  INFO_TEXT: "You won't be able to view this API key again.",
  DESCRIPTION: [
    { content: 'Save your key somewhere safe as ' },
    { content: "you won't be able to view it again", bold: true },
    {
      content:
        ". Keep it secure, as it allows others to make requests on your behalf. If lost, you'll need to generate a new one.",
    },
  ] as { content: string; bold?: boolean }[],
  BUTTON_TEXT: 'Done',
  COPY_BUTTON_TEXT: 'Copy',
  COPY_BUTTON_ALT_TEXT: 'Copied!',
};

export const API_KEY_TABLE_COLUMN_LABELS = {
  NAME: 'Name',
  KEY: 'Key',
  EXPIRES: 'Expires',
  WORKSPACES: 'Workspaces',
  ROLE: 'Role',
  CREATED_BY: 'Created by',
};

export const API_KEY_REVOCATION = {
  MODAL_TITLE: 'Revoke API Key?',
  BUTTON_LABEL: 'Revoke Key',
  WARNING: 'This action is irreversible.',
  CONFIRMATION:
    'Revoking the key %api_key% immediately disables it so it cannot be used again. Any connected services will lose access.',
};

export const API_KEYS_DEFAULT_PAGE_SIZE = 10;

export const API_KEYS_HEADER = 'API Keys';
export const API_KEYS_SUBHEADING =
  "Generate an API key to easily connect deepset AI Platform to your solution. You'll need it to use API endpoints or when working in an SDK. You can set the API key permissions and specify the workspaces it can access.";
export const API_KEY_CREATE_BUTTON_LABEL = 'Create API Key';

export const EMPTY_API_KEYS_LIST_SECTION = {
  TITLE: 'API Keys',
  DESCRIPTION: [
    {
      paragraph: API_KEYS_SUBHEADING,
    },
  ],
};

export const API_KEY_ACTION_MESSAGES = {
  CREATED: 'API key created.',
  DELETED: 'Api key revoked.',
  UPDATED: 'API key updated.',
};

export const USER_ROLE_OPTIONS: SelectProps['options'] = Object.entries(UserRolesMap).map(
  ([role, label]) => ({
    label,
    value: role,
  }),
);

export const DEFAULT_WORKSPACE_OPTION: DefaultOptionType = {
  label: 'All',
  value: 'all',
};

export const API_KEY_INITIAL_VALUES = {
  name: '',
  never_expires: false,
  expires: true,
  expires_at: undefined,
  role: UserRoles.EDITOR,
  workspaces: [DEFAULT_WORKSPACE_OPTION.value],
  personal: true,
};

export const OPTIONAL_FIELD_MARK = '(optional)';
export const API_KEY_EXPIRES_AT_VALIDATION_ERROR = 'Choose a date';

export enum ApiKeyStatus {
  All = 'all',
  Active = 'active',
  Expired = 'expired',
  ExpiresSoon = 'expires_soon',
}

// See https://day.js.org/docs/en/manipulate/add#list-of-all-available-units
export const API_KEY_EXPIRES_SOON_DELTA: { unit: ManipulateType; quantity: number } = {
  unit: 'week',
  quantity: 2,
};

export enum ApiKeySort {
  NEWEST = 'newest',
  OLDEST = 'oldest',
  NAME = 'name',
  NAME_DESC = 'name_desc',
  EXPIRES_FIRST = 'expires_first',
  EXPIRES_LAST = 'expires_last',
}

export const API_KEY_FILTERS: FiltersProp = [
  {
    key: 'status',
    style: { minWidth: '87px' },
    title: 'Status',
    type: FilterType.SELECT,
    options: [
      { key: ApiKeyStatus.All, label: 'All' },
      { key: ApiKeyStatus.Active, label: 'Active' },
      { key: ApiKeyStatus.Expired, label: 'Expired' },
      { key: ApiKeyStatus.ExpiresSoon, label: 'Expires soon' },
    ],
  },
];

export const API_KEY_DEFAULT_FILTER: SelectedFilters = {
  status: [
    {
      label: 'All',
      labelPrefix: 'Status',
      value: ApiKeyStatus.All,
      ...(API_KEY_FILTERS[0] as Pick<FilterProp, 'key' | 'type'>),
    },
  ],
};

export const API_KEY_SORTING = [
  { key: ApiKeySort.NAME, label: 'Name (A to Z)' },
  { key: ApiKeySort.NAME_DESC, label: 'Name (Z to A)' },
  { key: ApiKeySort.NEWEST, label: 'Newest to oldest' },
  { key: ApiKeySort.OLDEST, label: 'Oldest to newest' },
  { key: ApiKeySort.EXPIRES_FIRST, label: 'Expires first' },
  { key: ApiKeySort.EXPIRES_LAST, label: 'Expires last' },
];

export const API_KEY_SORTING_PARAMS = {
  [ApiKeySort.NEWEST]: {
    field: 'created_at',
    order: 'DESC',
  },
  [ApiKeySort.OLDEST]: {
    field: 'created_at',
    order: 'ASC',
  },
  [ApiKeySort.NAME]: {
    field: 'name',
    order: 'ASC',
  },
  [ApiKeySort.NAME_DESC]: {
    field: 'name',
    order: 'DESC',
  },
  [ApiKeySort.EXPIRES_FIRST]: {
    field: 'expires_at',
    order: 'ASC',
  },
  [ApiKeySort.EXPIRES_LAST]: {
    field: 'expires_at',
    order: 'DESC',
  },
};

export enum ApiKeyType {
  Personal = 'personal',
  Service = 'service',
}

export const API_KEY_TYPE_LABELS = {
  [ApiKeyType.Personal]: 'Personal Key',
  [ApiKeyType.Service]: 'Service Key',
};

export const API_KEY_TYPE_DESCRIPTIONS = {
  [ApiKeyType.Personal]:
    'Bound to your user account. Use this for development or tools acting on your behalf.',
  [ApiKeyType.Service]:
    'Linked to a service or automation. Ideal for backend systems or CI/CD pipelines.',
};

export const API_KEY_TABLE_DATE_FORMAT = 'DD-MM-YYYY';

export const API_KEY_ABOUT_TO_EXPIRE_LABEL = 'Expires soon';

export const API_KEY_DEFAULT_MASK = '***********';
